import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserService, UserInfo, GetUserDetailsResponse } from '../../../@backoffice/services/user.service';
import { LoginService } from '../../auth/login/login.service';
import { PermissionService } from '../../../@backoffice/services/permission.service';
import { ApiResponseError } from '../../../@backoffice/models/api-response.model';
import { buildClientRoute } from '../utils/client-route.util';

/**
 * User Resolver
 * 
 * This resolver pre-fetches user details before the route activates.
 * It ensures that user information is available when the MasterComponent loads.
 * 
 * If the user details cannot be fetched, it redirects to login.
 * 
 * Usage: Add `resolve: { userInfo: userResolver }` to the route configuration.
 */
export const userResolver: ResolveFn<UserInfo> = (route, state) => {
  const userService = inject(UserService);
  const loginService = inject(LoginService);
  const router = inject(Router);
  const permissionService = inject(PermissionService);

  const bailToLogin = (reason: string): Observable<never> => {
    console.warn(`UserResolver: ${reason}, redirecting to login`);
    loginService.logout();
    router.navigate(buildClientRoute(router, ['/login']));
    return throwError(() => new Error(reason));
  };

  const loadUserInfo = (): Observable<UserInfo> => {
    // Get nameIdentifier from stored user info first
    const storedUserInfo = loginService.getUserInfo() as { nameIdentifier?: string } | null;
    if (!storedUserInfo || !storedUserInfo.nameIdentifier) {
      console.error('UserResolver: No nameIdentifier found in stored user info');
      return bailToLogin('No nameIdentifier found');
    }

    const nameIdentifier = storedUserInfo.nameIdentifier;

    // Always fetch fresh user details and initialize permissions via PermissionService
    return permissionService.ensurePermissions(nameIdentifier).pipe(
      map((userInfo: UserInfo) => userInfo),
      catchError((error) => {
        console.error('UserResolver: Error fetching user details:', error);

        // Extract status code from various error formats
        let statusCode: number | undefined;
        if (error instanceof ApiResponseError) {
          statusCode = error.statusCode;
        } else if (error?.status) {
          statusCode = error.status;
        } else if (error?.statusCode) {
          statusCode = error.statusCode;
        } else if ((error?.error as any)?.statusCode) {
          statusCode = (error.error as any).statusCode;
        }

        // Check if it's an authentication error (401/403)
        const isAuthError = statusCode === 401;

        // Only redirect to login if it's an authentication error
        // For other errors (network, server errors), allow route to proceed
        // Component can handle the error state
        if (isAuthError) {
          console.warn('UserResolver: Authentication error, redirecting to login');
          router.navigate(buildClientRoute(router, ['/login']));
          return throwError(() => new Error('Authentication failed'));
        }

        // For non-auth errors, throw the error but don't redirect
        // This allows the component to handle the error gracefully
        console.warn('UserResolver: Non-auth error, allowing route to proceed');
        return throwError(() => error);
      })
    );
  };

  const authState = loginService.getAuthState();

  if (authState === 'invalid') {
    return bailToLogin('User not authenticated');
  }

  // Access token lapsed but the refresh token is still usable, so renew instead of
  // ending the session (TDA3-T473). authGuard normally handles this first; this covers
  // a token that expires between the guard running and the resolver running.
  if (authState === 'expired') {
    return loginService.refreshAccessTokenShared().pipe(
      catchError(() => bailToLogin('Session refresh failed')),
      switchMap(() => loadUserInfo())
    );
  }

  return loadUserInfo();
};
